<template>
	<div class="finance-panel-box" @click.prevent="showDetail">
		<div class="icon" v-html="data.icon"></div>
		<h4 class="finance-panel-title">{{ data.name }}</h4>
		<p class="amount">{{ amount }}</p>
		<button type="button" v-if="showRemoveButton" class="remove-item" @click.stop.prevent="removeItem">
			<i class="fa-regular fa-trash"></i>
		</button>
	</div>
</template>

<script>
import pageFunctionsMixin from "@/mixins/pageFunctionsMixin";

export default {
	name: "FinancePanelBox",
	props: ['data'],
	mixins: [pageFunctionsMixin],
	computed: {
		amount() {
			let formatted = this.formatNumber(this.data.value);

			return `${formatted} ${process.env.VUE_APP_CURRENCY}`;
		},
		showRemoveButton() {
			return this.data.type !== 'contract';
		},
	},
	methods: {
		removeItem() {
			const configMap = {
				property: {
					path: 'properties',
					commits: ['REMOVE_PROPERTY']
				},
				debt: {
					path: 'documents',
					commits: ['REMOVE_DEBT', 'REMOVE_PAYMENT']
				},
				payment: {
					path: 'documents',
					commits: ['REMOVE_PAYMENT', 'REMOVE_DEBT']
				}
			};

			const config = configMap[this.data.finance_type];
			if (!config) {
				return;
			}

			this.$swal.fire({
				icon: 'question',
				title: this.$t('general.delete_confirm_title'),
				text: this.$t('general.delete_confirm_text_property'),
				showDenyButton: true,
				confirmButtonText: this.$t('general.yes'),
				denyButtonText: this.$t('general.no'),
			}).then((result) => {
				if (result.isConfirmed) {
					this.axios.post(`/api/${config.path}/${this.data.id}`, {
						_method: 'DELETE'
					}).then(() => {
						config.commits.forEach(commit => {
							this.$store.commit(commit, {
								id: this.data.id,
								type: this.data.type,
							});
						});
					});
				}
			});
		},
		showDetail() {
			let data;
			if (this.data.finance_type === 'property') {
				if (this.data.property_type === 'investment_account') {
					this.$router.push(`${this.$t(`routes.investments_${this.data.investment_account_type}.path`)}/${this.data.id}`);
					return;
				}

				data = {
					path: 'properties',
					commit: 'SHOW_PROPERTY_EDIT_FORM',
					response: 'property'
				};
			} else if (this.data.finance_type === 'debt' || this.data.finance_type === 'payment') {
				if (this.data.type === 'document') {
					data = {
						path: 'documents',
						commit: 'SHOW_DOCUMENTS_EDIT_FORM',
						response: 'document',
					};
				} else {
					data = {
						path: 'contracts',
						commit: 'SHOW_CONTRACTS_EDIT_FORM',
						response: 'contract'
					};
				}
			}

			this.$store.commit('SET_APP_LOADING');
			this.axios.get(`/api/${data.path}/${this.data.id}`).then((response) => {
				let model = response.data[data.response];

				if (this.data.finance_type === 'debt') {
					model.debt = true;
				} else if (this.data.finance_type === 'payment') {
					model.payment = true;
				}
				this.$store.commit(data.commit, model);
				this.$store.commit('UNSET_APP_LOADING');
			}).catch(() => {
				this.$store.commit('UNSET_APP_LOADING');
				this.$swal.fire({
					icon: 'error',
					title: 'Oops',
					text: this.$t('general.default_error')
				});
			});
		}
	}
}
</script>

<style scoped lang="scss">
.light-mode {
	.finance-panel-box {
		background: #F0F0F2;

		.amount,
		.icon {
			color: var(--background-color, $backgroundColor);
		}
	}
}

.finance-panel-box {
	padding: 25px 30px;
	background: rgba(var(--white-color-rgb), .06);
	border-radius: 5px;
	cursor: pointer;
	transition: $animationDefaultDuration;
	position: relative;
	display: flex;
	align-items: center;

	&:hover {
		.remove-item {
			opacity: 1;
			pointer-events: auto;
		}
	}

	.remove-item {
		background: var(--white-color, $whiteColor);
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 100%;
		width: 38px;
		height: 38px;
		font-size: 14px;
		border: 5px solid var(--background-color, $backgroundColor);
		color: var(--primary-color, $primaryColor);
		cursor: pointer;
		transition: $animationDefaultDuration;
		position: absolute;
		right: -19px;
		top: -19px;
		opacity: 0;
		pointer-events: none;

		&:hover {
			color: var(--white-color, $whiteColor);
			background: var(--primary-color, $primaryColor);
		}
	}

	&:before {
		content: '';
		display: block;
		position: absolute;
		top: -4px;
		right: -4px;
		bottom: -4px;
		left: -4px;
		pointer-events: none;
		border: 2px solid var(--secondary-color, $primaryColor);
		border-radius: 8px;
		opacity: 0;
		transition: $animationDefaultDuration;
	}

	&:hover {
		&:before {
			opacity: 1;
		}
	}

	.icon {
		font-size: 18px;
		color: var(--white-color, $whiteColor);
		width: 20px;
		margin-right: 15px;
	}

	.amount {
		margin-left: auto;
		margin-bottom: 0;
		color: var(--white-color, $whiteColor);
		white-space: nowrap;
	}

	.finance-panel-title {
		margin-bottom: 0;
		font-size: 16px;
		font-weight: 600;
		margin-right: 20px;
	}
}
</style>